import { byId, isAr, formatNumber, attachCopyButton, setupSegmented, trackEvent } from './calc-common.js';
import Big from 'big.js';

const GRADE_SCALES = {
  '4': [
    { label: 'A+ (4.00)', points: 4.0 },
    { label: 'A (3.75)', points: 3.75 },
    { label: 'B+ (3.50)', points: 3.5 },
    { label: 'B (3.00)', points: 3.0 },
    { label: 'C+ (2.50)', points: 2.5 },
    { label: 'C (2.00)', points: 2.0 },
    { label: 'D+ (1.50)', points: 1.5 },
    { label: 'D (1.00)', points: 1.0 },
    { label: 'F (0.00)', points: 0.0 }
  ],
  '5': [
    { label: 'A+ (5.00) [95-100]', points: 5.0 },
    { label: 'A (4.75) [90-94]', points: 4.75 },
    { label: 'B+ (4.50) [85-89]', points: 4.5 },
    { label: 'B (4.00) [80-84]', points: 4.0 },
    { label: 'C+ (3.50) [75-79]', points: 3.5 },
    { label: 'C (3.00) [70-74]', points: 3.0 },
    { label: 'D+ (2.50) [65-69]', points: 2.5 },
    { label: 'D (2.00) [60-64]', points: 2.0 },
    { label: 'F (1.00) [<60]', points: 1.0 }
  ],
  '100': [
    { label: '90 - 100% (ممتاز / Excellent)', points: 95 },
    { label: '80 - 89% (جيد جداً / Very Good)', points: 85 },
    { label: '70 - 79% (جيد / Good)', points: 75 },
    { label: '60 - 69% (مقبول / Pass)', points: 65 },
    { label: '< 60% (راسب / Fail)', points: 50 }
  ]
};

let currentScale = '5';
let courses = [
  { id: 1, name: isAr ? 'مقرر 1' : 'Course 1', credits: 3, grade: 5.0 },
  { id: 2, name: isAr ? 'مقرر 2' : 'Course 2', credits: 3, grade: 4.75 },
  { id: 3, name: isAr ? 'مقرر 3' : 'Course 3', credits: 4, grade: 4.5 },
  { id: 4, name: isAr ? 'مقرر 4' : 'Course 4', credits: 2, grade: 4.0 }
];
let nextId = 5;

export function initGpaCalculator() {
  const container = byId('gpa-calculator-app');
  if (!container) return;

  setupSegmented('gpa-scale-tabs', (val) => {
    currentScale = val;
    // Map existing courses to closest valid points or reset to top
    const defaultGrade = GRADE_SCALES[currentScale][0].points;
    courses.forEach(c => { c.grade = defaultGrade; });
    renderCourseRows();
    calculateGpa();
  });

  const addBtn = byId('gpa-add-course');
  if (addBtn) {
    addBtn.addEventListener('click', () => {
      const defaultGrade = GRADE_SCALES[currentScale][0].points;
      courses.push({
        id: nextId++,
        name: (isAr ? 'مقرر ' : 'Course ') + courses.length + 1,
        credits: 3,
        grade: defaultGrade
      });
      renderCourseRows();
      calculateGpa();
    });
  }

  const prevGpaInput = byId('gpa-prev-gpa');
  const prevCreditsInput = byId('gpa-prev-credits');
  [prevGpaInput, prevCreditsInput].forEach(inp => {
    if (inp) {
      inp.addEventListener('input', calculateGpa);
    }
  });

  attachCopyButton(byId('gpa-copy-btn'), () => {
    const semGpa = byId('gpa-sem-result')?.textContent || '';
    const cumGpa = byId('gpa-cum-result')?.textContent || '';
    return isAr
      ? `المعدل الفصلي: ${semGpa}\nالمعدل التراكمي: ${cumGpa}`
      : `Semester GPA: ${semGpa}\nCumulative GPA: ${cumGpa}`;
  });

  const printBtn = byId('gpa-print-btn');
  if (printBtn) {
    printBtn.addEventListener('click', () => {
      window.print();
    });
  }

  renderCourseRows();
  calculateGpa();
}

function renderCourseRows() {
  const tbody = byId('gpa-courses-body');
  if (!tbody) return;

  tbody.innerHTML = courses.map((c) => {
    const options = GRADE_SCALES[currentScale].map(opt => `
      <option value="${opt.points}" ${Number(c.grade) === Number(opt.points) ? 'selected' : ''}>
        ${opt.label}
      </option>
    `).join('');

    return `
      <tr data-course-id="${c.id}" class="kz-course-row">
        <td>
          <input type="text" class="kz-input kz-input-sm course-name" value="${c.name}" placeholder="${isAr ? 'اسم المادة' : 'Course Name'}" style="width:100%;">
        </td>
        <td>
          <input type="number" min="0.5" max="12" step="0.5" class="kz-input kz-input-sm course-credits" value="${c.credits}" style="width:80px; text-align:center;">
        </td>
        <td>
          <select class="kz-input kz-input-sm course-grade" style="width:100%;">
            ${options}
          </select>
        </td>
        <td style="text-align:center;">
          <button type="button" class="btn-icon-subtle delete-course-btn" title="${isAr ? 'حذف المقرر' : 'Delete course'}" aria-label="${isAr ? 'حذف المقرر' : 'Delete course'}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 6h18m-2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </button>
        </td>
      </tr>
    `;
  }).join('');

  // Attach event handlers
  tbody.querySelectorAll('.course-name').forEach((inp, idx) => {
    inp.addEventListener('input', (e) => {
      courses[idx].name = e.target.value;
    });
  });

  tbody.querySelectorAll('.course-credits').forEach((inp, idx) => {
    inp.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      courses[idx].credits = isNaN(val) ? 0 : val;
      calculateGpa();
    });
  });

  tbody.querySelectorAll('.course-grade').forEach((sel, idx) => {
    sel.addEventListener('change', (e) => {
      courses[idx].grade = parseFloat(e.target.value);
      calculateGpa();
    });
  });

  tbody.querySelectorAll('.delete-course-btn').forEach((btn, idx) => {
    btn.addEventListener('click', () => {
      if (courses.length <= 1) return; // Keep at least one
      courses.splice(idx, 1);
      renderCourseRows();
      calculateGpa();
    });
  });
}

function calculateGpa() {
  let semCredits = 0;
  let semWeightedPoints = Big(0);

  courses.forEach(c => {
    const credits = Number(c.credits) || 0;
    const grade = Number(c.grade) || 0;
    semCredits += credits;
    semWeightedPoints = semWeightedPoints.plus(Big(credits).times(Big(grade)));
  });

  const semGpa = semCredits > 0
    ? semWeightedPoints.div(Big(semCredits)).toFixed(2)
    : '0.00';

  const semResultEl = byId('gpa-sem-result');
  if (semResultEl) semResultEl.textContent = semGpa;

  const semCreditsEl = byId('gpa-sem-credits');
  if (semCreditsEl) semCreditsEl.textContent = semCredits.toString();

  // Cumulative GPA
  const prevGpaVal = parseFloat(byId('gpa-prev-gpa')?.value);
  const prevCreditsVal = parseFloat(byId('gpa-prev-credits')?.value);

  const prevGpa = (!isNaN(prevGpaVal) && prevGpaVal >= 0) ? prevGpaVal : null;
  const prevCredits = (!isNaN(prevCreditsVal) && prevCreditsVal > 0) ? prevCreditsVal : null;

  let cumGpa = semGpa;
  let totalCumCredits = semCredits;

  if (prevGpa !== null && prevCredits !== null) {
    totalCumCredits = semCredits + prevCredits;
    const prevPoints = Big(prevGpa).times(Big(prevCredits));
    const totalPoints = semWeightedPoints.plus(prevPoints);
    cumGpa = totalCumCredits > 0 ? totalPoints.div(Big(totalCumCredits)).toFixed(2) : '0.00';
  }

  const cumResultEl = byId('gpa-cum-result');
  if (cumResultEl) cumResultEl.textContent = cumGpa;

  const cumCreditsEl = byId('gpa-cum-credits');
  if (cumCreditsEl) cumCreditsEl.textContent = totalCumCredits.toString();

  // Rating label
  const ratingEl = byId('gpa-rating-badge');
  if (ratingEl) {
    ratingEl.textContent = getGradeRating(parseFloat(cumGpa), currentScale);
  }
}

function getGradeRating(gpa, scale) {
  if (scale === '5') {
    if (gpa >= 4.5) return isAr ? 'ممتاز مرتفع / ممتاز (First Class)' : 'Excellent';
    if (gpa >= 3.75) return isAr ? 'جيد جداً (Very Good)' : 'Very Good';
    if (gpa >= 2.75) return isAr ? 'جيد (Good)' : 'Good';
    if (gpa >= 2.0) return isAr ? 'مقبول (Pass)' : 'Pass';
    return isAr ? 'راسب / إنذار (Fail/Probation)' : 'Fail/Probation';
  } else if (scale === '4') {
    if (gpa >= 3.5) return isAr ? 'ممتاز (Excellent)' : 'Excellent';
    if (gpa >= 3.0) return isAr ? 'جيد جداً (Very Good)' : 'Very Good';
    if (gpa >= 2.5) return isAr ? 'جيد (Good)' : 'Good';
    if (gpa >= 2.0) return isAr ? 'مقبول (Pass)' : 'Pass';
    return isAr ? 'راسب (Fail)' : 'Fail';
  } else {
    if (gpa >= 85) return isAr ? 'ممتاز (Excellent)' : 'Excellent';
    if (gpa >= 75) return isAr ? 'جيد جداً (Very Good)' : 'Very Good';
    if (gpa >= 65) return isAr ? 'جيد (Good)' : 'Good';
    if (gpa >= 60) return isAr ? 'مقبول (Pass)' : 'Pass';
    return isAr ? 'راسب (Fail)' : 'Fail';
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGpaCalculator);
  } else {
    initGpaCalculator();
  }
}
